import React, { useState, useMemo } from 'react';
import { Mail, Loader2, Users, UserCheck } from 'lucide-react';
import ApiService from '../../../services/apiService';

const NotificationsPage = ({ companyData, addMessage, selectedCompany, directors, shareholders }) => {
    const [selectedRecipients, setSelectedRecipients] = useState([]);
    const [loading, setLoading] = useState(false);

    const getDirectorName = (director) => {
        const { first_name, middle_name, last_name } = director;
        const names = [first_name, middle_name, last_name].filter(name => name && name.trim() !== '');
        return names.join(' ');
    };

    const getShareholderName = (shareholder) => {
        if (shareholder.entity_type === 'Individual') {
            return shareholder.name || [shareholder.first_name, shareholder.middle_name, shareholder.last_name].filter(Boolean).join(' ');
        }
        return shareholder.name || 'Unknown Shareholder';
    };
    
    const getDirectorTypes = (director) => {
        const types = [];
        if (director.is_director) types.push('Director');
        if (director.is_alternate_director) types.push('Alternate Director');
        if (director.is_company_secretary) types.push('Company Secretary');
        return types.join(', ');
    };

    const allRecipients = useMemo(() => {
        // Support both props format (directors/shareholders) and companyData format
        const directorsList = directors || companyData?.directors || [];
        const shareholdersList = shareholders || companyData?.shareholders || [];
        
        // Filter to only include directors/shareholders for the selected company
        const filteredDirectors = selectedCompany 
            ? directorsList.filter(d => d.company_id === selectedCompany.id)
            : directorsList;
        
        const filteredShareholders = selectedCompany
            ? shareholdersList.filter(s => s.company_id === selectedCompany.id)
            : shareholdersList;

        const directorList = filteredDirectors.map(d => ({
            id: `director-${d.id}`,
            name: getDirectorName(d),
            email: d.email,
            roleOrType: getDirectorTypes(d),
            entity: 'director',
            originalId: d.id
        }));

        const shareholderList = filteredShareholders.map(s => ({
            id: `shareholder-${s.id}`,
            name: getShareholderName(s),
            email: s.email,
            roleOrType: `Shareholder${s.entity_type ? ` (${s.entity_type})` : s.type ? ` (${s.type})` : ''}`,
            entity: 'shareholder',
            originalId: s.id
        }));

        return [...directorList, ...shareholderList].sort((a, b) => a.name.localeCompare(b.name));
    }, [directors, shareholders, companyData, selectedCompany]);

    const handleSelectRecipient = (recipientId) => {
        setSelectedRecipients(prev =>
            prev.includes(recipientId)
                ? prev.filter(id => id !== recipientId)
                : [...prev, recipientId]
        );
    };

    const handleSelectAll = (entityType) => {
        const idsToAdd = allRecipients
            .filter(r => r.entity === entityType && r.email)
            .map(r => r.id);
        setSelectedRecipients(prev => [...new Set([...prev, ...idsToAdd])]);
    };

    const handleDeselectAll = (entityType) => {
        setSelectedRecipients(prev => prev.filter(id => !id.startsWith(entityType)));
    };

    const handleSendNotifications = async () => {
        // FIX: Add a guard to ensure selectedCompany exists
        if (!selectedCompany) {
            addMessage('No company is selected.', 'error');
            return;
        }

        const selectedShareholderIds = selectedRecipients
            .filter(id => id.startsWith('shareholder-'))
            .map(id => parseInt(id.replace('shareholder-', ''), 10))
            .filter(id => !isNaN(id));
            
        const selectedDirectorIds = selectedRecipients
            .filter(id => id.startsWith('director-'))
            .map(id => parseInt(id.replace('director-', ''), 10))
            .filter(id => !isNaN(id));

        if (selectedShareholderIds.length === 0 && selectedDirectorIds.length === 0) {
            addMessage('Please select at least one recipient.', 'error');
            return;
        }

        setLoading(true);
        let successCount = 0;
        let errorMessages = [];
        
        try {
            if (selectedShareholderIds.length > 0) {
                const shareholderResponse = await ApiService.sendShareholderNotifications(selectedCompany.id, selectedShareholderIds);
                successCount += shareholderResponse.sent_count || 0;
                if(shareholderResponse.failed_count > 0) {
                    errorMessages.push(`Failed to send to ${shareholderResponse.failed_count} shareholder(s).`);
                }
            }

            if (selectedDirectorIds.length > 0) {
                const directorResponse = await ApiService.sendDirectorNotifications(selectedCompany.id, selectedDirectorIds);
                successCount += directorResponse.sent_count || 0;
                 if(directorResponse.failed_count > 0) {
                    errorMessages.push(`Failed to send to ${directorResponse.failed_count} director(s).`);
                }
            }
            
            if(successCount > 0) {
                addMessage(`Successfully sent notifications to ${successCount} recipient(s).`, 'success');
            }
            if(errorMessages.length > 0) {
                addMessage(errorMessages.join(' '), 'error');
            }
            setSelectedRecipients([]);

        } catch (error) {
            console.error('Error sending notifications:', error);
            const errorMessage = error.message || error.data?.error || 'Failed to send notifications';
            addMessage(`Failed to send notifications: ${errorMessage}`, 'error');
        } finally {
            setLoading(false);
        }
    };
    
    if (!selectedCompany) {
        return (
            <div className="space-y-6">
                <h2 className="text-2xl font-bold">Notify Directors or Shareholders</h2>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <p className="text-yellow-800">Please select a company to view available directors and shareholders.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <h2 className="text-2xl font-bold">Notify Directors or Shareholders</h2>
            <p className="text-gray-600">Select recipients to send them an email summary of their holdings or role.</p>
            
            {allRecipients.length === 0 && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <p className="text-gray-600">No directors or shareholders found for {selectedCompany.name}.</p>
                </div>
            )}
            <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-xl font-bold mb-4">Select Recipients</h3>
                
                <div className="flex flex-wrap gap-x-6 gap-y-2 mb-4 border-b pb-4">
                    <div className="flex items-center space-x-2">
                        <span className="text-sm font-semibold text-gray-700">Directors:</span>
                        <button onClick={() => handleSelectAll('director')} className="text-xs bg-gray-200 text-gray-800 px-2 py-1 rounded hover:bg-gray-300">Select All</button>
                        <button onClick={() => handleDeselectAll('director')} className="text-xs bg-gray-200 text-gray-800 px-2 py-1 rounded hover:bg-gray-300">Select None</button>
                    </div>
                    <div className="flex items-center space-x-2">
                        <span className="text-sm font-semibold text-gray-700">Shareholders:</span>
                        <button onClick={() => handleSelectAll('shareholder')} className="text-xs bg-gray-200 text-gray-800 px-2 py-1 rounded hover:bg-gray-300">Select All</button>
                        <button onClick={() => handleDeselectAll('shareholder')} className="text-xs bg-gray-200 text-gray-800 px-2 py-1 rounded hover:bg-gray-300">Select None</button>
                    </div>
                </div>

                <div className="space-y-3 max-h-80 overflow-y-auto pr-2">
                    {allRecipients.map(recipient => (
                        <div key={recipient.id} className="flex items-center p-2 rounded-md hover:bg-gray-50">
                            <input
                                type="checkbox"
                                id={`recipient-${recipient.id}`}
                                checked={selectedRecipients.includes(recipient.id)}
                                onChange={() => handleSelectRecipient(recipient.id)}
                                className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                disabled={!recipient.email}
                            />
                            <label htmlFor={`recipient-${recipient.id}`} className="ml-3 text-sm font-medium text-gray-700 flex-1 cursor-pointer">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <span className="font-semibold">{recipient.name}</span>
                                        <span className="text-gray-500 ml-2">({recipient.email || 'No Email'})</span>
                                    </div>
                                    <span className={`text-xs px-2 py-1 rounded-full ${
                                        recipient.entity === 'director' ? 'bg-indigo-100 text-indigo-800' : 'bg-blue-100 text-blue-800'
                                    }`}>
                                        {recipient.entity === 'director' ? <UserCheck className="h-3 w-3 inline mr-1" /> : <Users className="h-3 w-3 inline mr-1" />}
                                        {recipient.roleOrType}
                                    </span>
                                </div>
                            </label>
                        </div>
                    ))}
                </div>
                <button
                    onClick={handleSendNotifications}
                    className="mt-6 w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 flex items-center justify-center disabled:bg-gray-400"
                    disabled={loading || selectedRecipients.length === 0}
                >
                    {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Mail className="h-5 w-5 mr-2" />}
                    Send Notification ({selectedRecipients.length})
                </button>
            </div>
        </div>
    );
};

export default NotificationsPage;
